import React from 'react';
import PropTypes from 'prop-types';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Table, TableRow, TableCell, TableBody, Typography, Box } from '@material-ui/core';
import useStyles from './VerifyResultStyles';
import LogViewer from './LogViewer';
import VerifyResultTableShell from './VerifyResultTableShell';

/**
 * Render a single scripted test result entry.
 */
function ScriptedResultEntry({ result }) {
    const classes = useStyles();

    if (!result) {
        return (
            <Typography variant="body2" color="textSecondary">
                <Trans>No results</Trans>
            </Typography>
        );
    }

    if (result.type === 'empty') {
        return (
            <Typography variant="body2" color="textSecondary">
                {result.message}
            </Typography>
        );
    }

    if (result.type === 'failure') {
        return (
            <Box>
                <Typography variant="body2" color="error">
                    {result.errorCode}
                </Typography>
                {result.errorValue && <Typography variant="body2">{result.errorValue}</Typography>}
                {result.errorTrace && (
                    <Typography variant="body2" component="pre" style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                        {result.errorTrace}
                    </Typography>
                )}
                <LogViewer logFile={result.logFile} />
            </Box>
        );
    }

    // Success result — metrics table + log
    return (
        <Box>
            {result.metrics?.length > 0 && (
                // width: auto prevents the 100% MUI default from inflating the outer table's max-content
                // width, which would otherwise force the outer table to expand to container width.
                <Table size="small" className={classes.metricsTable} style={{ width: 'auto' }}>
                    <TableBody>
                        {result.metrics.map((metric) => (
                            <TableRow key={metric.key}>
                                <TableCell>{metric.key}:</TableCell>
                                <TableCell>{String(metric.value)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
            <LogViewer logFile={result.logFile} />
        </Box>
    );
}

ScriptedResultEntry.propTypes = {
    result: PropTypes.shape({
        type: PropTypes.oneOf(['empty', 'failure', 'success']),
        message: PropTypes.string,
        errorCode: PropTypes.string,
        errorValue: PropTypes.string,
        errorTrace: PropTypes.string,
        logFile: PropTypes.string,
        metrics: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.string,
                value: PropTypes.any,
            })
        ),
    }),
};

ScriptedResultEntry.defaultProps = {
    result: null,
};

/**
 * Render Scripted verify results — agent table with metrics/errors per agent.
 * portal/ui source: verifyScriptedTest.service.js buildTestResultHtml()
 */
function ScriptedResultTable({ resultData }) {
    const classes = useStyles();
    const { i18n } = useLingui();

    if (!resultData?.agentRows?.length) return null;

    const tableHeading = resultData.isSuccessful
        ? i18n._(t`Verify scripted test results`)
        : i18n._(t`Verify error messages`);

    return (
        <VerifyResultTableShell title={tableHeading}>
            {resultData.agentRows.map((agentRow) => {
                const [firstResult, ...restResults] = agentRow.results;
                return (
                    <React.Fragment key={agentRow.agentName}>
                        <TableRow>
                            <TableCell className={classes.pulseNameCell}>{agentRow.agentName}</TableCell>
                            <TableCell>
                                <ScriptedResultEntry result={firstResult} />
                            </TableCell>
                        </TableRow>
                        {restResults.map((result, idx) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <TableRow key={`${agentRow.agentName}-extra-${idx}`}>
                                <TableCell />
                                <TableCell>
                                    <ScriptedResultEntry result={result} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </React.Fragment>
                );
            })}
        </VerifyResultTableShell>
    );
}

ScriptedResultTable.propTypes = {
    resultData: PropTypes.shape({
        agentRows: PropTypes.array,
        isSuccessful: PropTypes.bool,
    }),
};

ScriptedResultTable.defaultProps = {
    resultData: null,
};

export default ScriptedResultTable;
